import { CloudErrors, FwmConstants, SharedComputerUtils } from '@portal/portal-core';
import { castArray } from 'lodash-es';
// Untyped third-party boundary: xml2js ships no bundled types and none are installed.
// It resolves to an untyped CJS module (so `declare module` augmentation is rejected),
// so we suppress the missing-declaration error here; the import resolves as `any`.
// @ts-expect-error -- no type declarations for xml2js (TS7016)
import { parseString } from 'xml2js';

const { numClientsMin, numConnectionsMin } = FwmConstants;

const {
  DUPLICATE_SHARED_COMPUTER_IDENTIFIER,
  DUPLICATE_SHARED_COMPUTER_LABEL,
  MAX_SHARED_COMPUTERS,
  MAX_SHARED_COMPUTER_CHANGES,
  NO_MATCHING_SHARED_COMUPUTER,
  NO_REMAINING_SEATS,
} = CloudErrors.sharedComputersErrorMessages;

interface LicenseFormValues {
  id?: string;
  label?: string;
  fingerprint?: string;
  startDate?: Date;
  endDate?: Date;
  contactLabel?: string;
  numConnections?: number;
  numClients?: number;
}

interface SharedComputer {
  id?: string;
  label?: string;
  [key: string]: unknown;
}

type ValidationError = Record<string, string>;

export const getValidationErrors = (
  { id, label, fingerprint, startDate, endDate, contactLabel, numConnections, numClients }: LicenseFormValues,
  sharedComputerState: SharedComputer[] = [],
): ValidationError[] => {
  const filteredSharedComputerState = id
    ? sharedComputerState.filter((comp) => comp.id !== id)
    : sharedComputerState;
  const errorMessageArray: ValidationError[] = [];
  // Label
  if (!label) {
    errorMessageArray.push({ label: 'Label is required' });
  }
  if (label && label.length > 256) {
    errorMessageArray.push({ label: 'Label must be less than 256 characters' });
  }
  if (filteredSharedComputerState.find((comp) => comp.label === label)) {
    errorMessageArray.push({ label: 'There is already a license with that label' });
  }
  // Contact Label
  if (!contactLabel) {
    errorMessageArray.push({ contactLabel: 'Contact label is required' });
  }
  if (contactLabel && contactLabel.length > 256) {
    errorMessageArray.push({ contactLabel: 'Contact label must be less than 256 characters' });
  }
  // Start Date
  if (!startDate) {
    errorMessageArray.push({ startDate: 'Start date is required' });
  }
  if (startDate && endDate && startDate > endDate) {
    errorMessageArray.push({ startDate: 'Start date must be before end date' });
  }

  // End Date
  if (!endDate) {
    errorMessageArray.push({ endDate: 'End date is required' });
  }
  if (startDate && endDate && startDate > endDate) {
    errorMessageArray.push({ endDate: 'End date must be after start date' });
  }

  // fingerprint
  if (!fingerprint) {
    errorMessageArray.push({ fingerprint: 'Fingerprint is required' });
  }
  if (fingerprint && fingerprint.length < 200) {
    errorMessageArray.push({ fingerprint: 'This entry is not a valid fingerprint' });
  }
  if (
    filteredSharedComputerState.find(
      (comp) =>
        SharedComputerUtils.getSharedComputerFwmEncryptedFingerprintIdentifierValue(comp) ===
        fingerprint,
    )
  ) {
    errorMessageArray.push({
      fingerprint: 'There is already a license with that fingerprint',
    });
  }

  // connections
  if (numConnections !== undefined && numConnections < numConnectionsMin) {
    errorMessageArray.push({
      numConnections: `Number of connections must be at least ${numConnectionsMin}`,
    });
  }

  // clients
  if (numClients !== undefined && numClients < numClientsMin) {
    errorMessageArray.push({
      numClients: `Number of clients must be at least ${numClientsMin}`,
    });
  }
  return errorMessageArray;
};

export const getApiErrorLabels = (apiErrors?: string | string[]): string[] => {
  const errorMessageArray: string[] = [];
  castArray(apiErrors).forEach((apiError) => {
    /* istanbul ignore next */
    switch (apiError) {
      case DUPLICATE_SHARED_COMPUTER_IDENTIFIER:
        errorMessageArray.push('There is already a shared computer with that hardware identifier.');
        break;
      case DUPLICATE_SHARED_COMPUTER_LABEL:
        errorMessageArray.push('There is already a shared computer with that label.');
        break;
      case MAX_SHARED_COMPUTERS:
        errorMessageArray.push('You have reached your max number of shared computers.');
        break;
      case MAX_SHARED_COMPUTER_CHANGES:
        errorMessageArray.push('You have reached your max number of shared computer changes.');
        break;
      case NO_MATCHING_SHARED_COMUPUTER:
        errorMessageArray.push('There is no matching shared computer');
        break;
      case NO_REMAINING_SEATS:
        errorMessageArray.push('You have reached your max number of seats.');
        break;
      default:
        errorMessageArray.push('An error occured.');
    }
  });
  return errorMessageArray;
};
/**
 * Select file(s).
 * @param {string} contentType The content type of files you wish to select. For instance, use "image/*" to select all types of images.
 * @returns {Promise<string>} A promise of a text file content converted to a string.
 */
export const selectTextFile = (contentType: string): Promise<string> =>
  new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = false;
    input.accept = contentType;

    input.onchange = (e) => {
      // getting a hold of the file reference
      const file = (e.target as HTMLInputElement).files![0];

      // setting up the reader
      const reader = new FileReader();
      reader.readAsText(file, 'UTF-8');

      // here we tell the reader what to do when it's done reading...
      reader.onload = (readerEvent) => resolve(readerEvent.target!.result as string);
    };

    input.click();
  });

/**
 * Convert an xml string to a JSON object
 * @param {string} xml an xml string
 * @returns {Promise<Object>} A promise of a JSON Object
 */
export const convertXmlToJson = (xml: string): Promise<Record<string, unknown>> =>
  new Promise((resolve, reject) => {
    parseString(
      xml,
      { explicitArray: false, mergeAttrs: true, explicitRoot: false },
      (err: Error | null, result: unknown) => {
        if (err) {
          reject(err);
        } else {
          resolve(result as Record<string, unknown>);
        }
      },
    );
  });
